// /opt/isp_erp/frontend/src/pages/ftth/ImportarRed.jsx
import React, { useEffect, useState, useRef } from 'react';
import {
  Box, Card, CardContent, Typography, Button, LinearProgress,
  Table, TableBody, TableCell, TableHead, TableRow, Chip, Alert,
  Stack, Divider, IconButton, Tooltip, Dialog, DialogTitle,
  DialogContent, DialogContentText, DialogActions,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import ReplayIcon from '@mui/icons-material/Replay';
import { ftthGeoAPI } from '../../api/ftth_geo.api';
import axios from '../../api/axios';

const estadoColor = (estado) => ({
  OK: 'success',
  PARCIAL: 'warning',
  ERROR: 'error',
  PROCESANDO: 'info',
  PENDIENTE: 'default',
}[estado] || 'default');

const formatearTamano = (bytes) => {
  if (!bytes) return '-';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
};

export default function ImportarRed() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const kmzRef = useRef();
  const csvRef = useRef();

  // --- Eliminar job ---
  const [confirmJob, setConfirmJob] = useState(null);
  const [deletingJob, setDeletingJob] = useState(false);
  const [borrarElementos, setBorrarElementos] = useState(true);
  const [borrarArchivo, setBorrarArchivo] = useState(true);

  // --- Reprocesar ---
  const [reprocesando, setReprocesando] = useState(null);

  // --- Ver log ---
  const [logJob, setLogJob] = useState(null);

  const cargarJobs = async () => {
    setLoading(true);
    try {
      const data = await ftthGeoAPI.getJobs();
      setJobs(data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Error al cargar jobs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { cargarJobs(); }, []);

  const handleUpload = async (file, tipo) => {
    if (!file) return;
    setError('');
    setSuccess('');
    setUploading(true);
    setProgress(0);
    try {
      const fn = tipo === 'csv' ? ftthGeoAPI.subirCsv : ftthGeoAPI.subirKmz;
      const res = await fn(file, (ev) => {
        if (ev.total) setProgress(Math.round((ev.loaded / ev.total) * 100));
      });
      setSuccess(
        `Archivo procesado. ` +
        `Total: ${res.total_features ?? res.total ?? 0}, ` +
        `NAPs: ${res.naps_creados ?? 0}, ` +
        `Elementos: ${res.elementos_creados ?? res.procesados ?? 0}, ` +
        `Cables: ${res.cables_creados ?? 0}, ` +
        `Errores: ${res.errores}`
      );
      await cargarJobs();
    } catch (e) {
      setError(e.response?.data?.detail || 'Error al subir archivo');
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  // --- Descargar archivo ---
  const handleDescargar = async (job) => {
    try {
      const res = await axios.get(
        `/ftth/geo/import/jobs/${job.id}/descargar`,
        { responseType: 'blob' }
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = job.nombre_archivo;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.response?.data?.detail || 'Error al descargar archivo');
    }
  };

  // --- Reprocesar ---
  const handleReprocesar = async (job) => {
    if (!window.confirm(
      `¿Reprocesar el archivo "${job.nombre_archivo}"?\n\n` +
      `Se creará un nuevo import con el mismo archivo.`
    )) return;
    setReprocesando(job.id);
    try {
      const res = await ftthGeoAPI.reprocesarImport(job.id);
      setSuccess(
        `Reprocesado OK. Nuevo job #${res.id} — ` +
        `Total: ${res.total_features}, Err: ${res.errores}`
      );
      await cargarJobs();
    } catch (e) {
      setError(e.response?.data?.detail || 'Error al reprocesar');
    } finally {
      setReprocesando(null);
    }
  };

  // --- Eliminar ---
  const handleEliminarJob = (job) => {
    setConfirmJob(job);
    setBorrarElementos(true);
    setBorrarArchivo(true);
  };

  const confirmarEliminarJob = async () => {
    if (!confirmJob) return;
    setDeletingJob(true);
    try {
      const res = await ftthGeoAPI.eliminarImport(
        confirmJob.id, borrarElementos, borrarArchivo
      );
      setSuccess(
        `Import eliminado. ` +
        `Elementos: ${res.elementos_borrados}, ` +
        `Cables: ${res.cables_borrados}` +
        (res.archivo_borrado ? ', Archivo borrado' : '')
      );
      setConfirmJob(null);
      await cargarJobs();
    } catch (e) {
      setError(e.response?.data?.detail || 'Error al eliminar import');
    } finally {
      setDeletingJob(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>📥 Importar Red FTTH</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Sube archivos <b>KMZ / KML</b> (Google Earth) para elementos de red y cables,
        o <b>CSV</b> para cargas masivas. Los archivos se guardan para poder descargarlos
        o reprocesarlos.
      </Typography>

      <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography variant="h6">🗺️ KMZ / KML</Typography>
            <Typography variant="caption" color="text.secondary">
              Puntos → elementos (OLT, Splitter, NAP, Poste).<br />
              NAPs se crean con <b>16 puertos libres</b> por defecto.<br />
              Líneas → cables (feeder, distribución, drop).
            </Typography>
            <Box sx={{ mt: 2 }}>
              <Button
                variant="contained"
                component="label"
                startIcon={<CloudUploadIcon />}
                disabled={uploading}
                fullWidth
              >
                Subir KMZ/KML
                <input
                  ref={kmzRef}
                  hidden
                  type="file"
                  accept=".kmz,.kml"
                  onChange={(e) => handleUpload(e.target.files[0], 'kmz')}
                />
              </Button>
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography variant="h6">📄 CSV</Typography>
            <Typography variant="caption" color="text.secondary">
              Columnas: <code>tipo, codigo, nombre, latitud, longitud, direccion, capacidad, zona, sector, ciudad, parent_codigo</code>
            </Typography>
            <Box sx={{ mt: 2 }}>
              <Button
                variant="contained"
                color="secondary"
                component="label"
                startIcon={<CloudUploadIcon />}
                disabled={uploading}
                fullWidth
              >
                Subir CSV
                <input
                  ref={csvRef}
                  hidden
                  type="file"
                  accept=".csv"
                  onChange={(e) => handleUpload(e.target.files[0], 'csv')}
                />
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Stack>

      {uploading && <LinearProgress variant="determinate" value={progress} sx={{ mb: 2 }} />}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <Divider sx={{ my: 2 }} />

      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="h6">Historial de importaciones</Typography>
        <Tooltip title="Refrescar">
          <IconButton onClick={cargarJobs}><RefreshIcon /></IconButton>
        </Tooltip>
      </Stack>

      <Card>
        <CardContent>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Archivo</TableCell>
                <TableCell align="right">Tamaño</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell align="right">Total</TableCell>
                <TableCell align="right">Proc</TableCell>
                <TableCell align="right">Err</TableCell>
                <TableCell>Creado</TableCell>
                <TableCell align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {jobs.map((j) => (
                <TableRow key={j.id}>
                  <TableCell>{j.id}</TableCell>
                  <TableCell>
                    <Tooltip title={j.nombre_archivo}>
                      <span>{j.nombre_archivo?.length > 30
                        ? j.nombre_archivo.slice(0, 30) + '…'
                        : j.nombre_archivo}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right">{formatearTamano(j.tamano_bytes)}</TableCell>
                  <TableCell>{j.tipo_archivo?.toUpperCase()}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={j.estado}
                      color={estadoColor(j.estado)}
                      onClick={() => j.log && setLogJob(j)}
                      style={{ cursor: j.log ? 'pointer' : 'default' }}
                    />
                  </TableCell>
                  <TableCell align="right">{j.total_features}</TableCell>
                  <TableCell align="right">{j.procesados}</TableCell>
                  <TableCell align="right">
                    {j.errores > 0 ? (
                      <Chip
                        size="small"
                        label={j.errores}
                        color="error"
                        onClick={() => setLogJob(j)}
                        style={{ cursor: 'pointer' }}
                      />
                    ) : j.errores}
                  </TableCell>
                  <TableCell>{new Date(j.creado_en).toLocaleString()}</TableCell>
                  <TableCell align="center">
                    {j.ruta_archivo && (
                      <Tooltip title="Descargar archivo original">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleDescargar(j)}
                        >
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {j.ruta_archivo && (
                      <Tooltip title="Reprocesar el archivo">
                        <span>
                          <IconButton
                            size="small"
                            color="warning"
                            onClick={() => handleReprocesar(j)}
                            disabled={reprocesando === j.id}
                          >
                            <ReplayIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                    <Tooltip title="Eliminar import">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleEliminarJob(j)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {jobs.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    No hay importaciones aún
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Diálogo de confirmación */}
      <Dialog
        open={!!confirmJob}
        onClose={() => !deletingJob && setConfirmJob(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DeleteIcon color="error" />
          ¿Eliminar esta importación?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Vas a eliminar el import <b>{confirmJob?.nombre_archivo}</b>.
            <br /><br />
            {borrarElementos ? (
              <>
                Se eliminarán también <b>los elementos geo y cables</b> creados por
                este archivo.<br />
                <b>Los NAPs clásicos NO se borran</b> (siguen con sus puertos y clientes).
              </>
            ) : (
              <>
                Solo se borrará el <b>registro del import</b>.<br />
                Los elementos en el mapa <b>se conservan</b>.
              </>
            )}
          </DialogContentText>

          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <input
                type="checkbox"
                id="chk-borrar-elementos"
                checked={borrarElementos}
                onChange={(e) => setBorrarElementos(e.target.checked)}
              />
              <label htmlFor="chk-borrar-elementos">
                <Typography variant="body2">
                  Borrar también los elementos del mapa (recomendado)
                </Typography>
              </label>
            </Box>
            {confirmJob?.ruta_archivo && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <input
                  type="checkbox"
                  id="chk-borrar-archivo"
                  checked={borrarArchivo}
                  onChange={(e) => setBorrarArchivo(e.target.checked)}
                />
                <label htmlFor="chk-borrar-archivo">
                  <Typography variant="body2">
                    Borrar también el archivo físico del disco
                  </Typography>
                </label>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmJob(null)} disabled={deletingJob}>
            Cancelar
          </Button>
          <Button
            onClick={confirmarEliminarJob}
            color="error"
            variant="contained"
            disabled={deletingJob}
          >
            {deletingJob ? 'Eliminando...' : 'Eliminar import'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de log */}
      <Dialog
        open={!!logJob}
        onClose={() => setLogJob(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Log del job #{logJob?.id} — {logJob?.nombre_archivo}
        </DialogTitle>
        <DialogContent>
          <Box
            component="pre"
            sx={{
              bgcolor: '#1e1e1e',
              color: '#d4d4d4',
              p: 2,
              borderRadius: 1,
              maxHeight: 500,
              overflow: 'auto',
              fontSize: 12,
              fontFamily: 'monospace',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {logJob?.log || '(sin log)'}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLogJob(null)}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
